import React, { useMemo } from "react";

import clsx from "clsx";

import { CustomMetrics } from "@features/custom-metrics";
import { type ColumnConfig } from "@features/sales-v3/hooks/use-table-state";
import { getCategoryIcon } from "@features/sales-v3/utils/category-icons-helper";
import {
  type MetricCategory,
  type SalesPageMetricWithChildren,
} from "@features/sales-v3/utils/metric-categorizer";
import { useDesignElementManager } from "@hooks/use-design-element-manager";

import { MetricSelection } from "./metric-selection";

type Props = {
  selectedCategory: MetricCategory;
  metrics: SalesPageMetricWithChildren[];
  selectedColumnConfigs: ColumnConfig[];
  selectManyMetrics: (metrics: SalesPageMetricWithChildren[]) => void;
  selectMetric: (metric: SalesPageMetricWithChildren) => void;
  clearAllByCategory: (category: MetricCategory) => void;
  deselectMetric: (metric: SalesPageMetricWithChildren) => void;
  listClassName?: string;
};

export const MetricSelectionGroup = ({
  selectedCategory,
  metrics,
  selectedColumnConfigs,
  selectManyMetrics,
  selectMetric,
  clearAllByCategory,
  deselectMetric,
  listClassName,
}: Props) => {
  const selectedMetricsSet = useMemo(
    () => new Set(selectedColumnConfigs.map(({ id }) => id)),
    [selectedColumnConfigs],
  );

  const [firstMetricsHalf, secondMetricsHalf] = divideIntoTwoColumns(metrics);
  const { designElementManager } = useDesignElementManager();

  return (
    <>
      <div className="flex items-center space-x-2 mt-3">
        {getCategoryIcon(designElementManager, selectedCategory)}
        <span className="text-lg font-bold">{selectedCategory}</span>
      </div>
      <div className="text-cosmic my-2 text-sm">
        <span
          className="underline cursor-pointer"
          onClick={() => selectManyMetrics(metrics)}
        >
          Select all
        </span>
        <span
          className="underline cursor-pointer ml-4"
          onClick={() => clearAllByCategory(selectedCategory)}
        >
          Clear all
        </span>
      </div>
      <div className={clsx("grid grid-cols-2 gap-x-2 text-sm", listClassName)}>
        <div>
          {firstMetricsHalf.map((metric) => (
            <div key={metric.id}>
              <MetricSelection
                isSelected={selectedMetricsSet.has(metric.id)}
                metric={metric}
                selectMetric={selectMetric}
                deselectMetric={deselectMetric}
              />
              {metric.children?.map((childMetric) => (
                <MetricSelection
                  key={childMetric.id}
                  className="ml-6"
                  isChild
                  isSelected={selectedMetricsSet.has(childMetric.id)}
                  metric={childMetric}
                  selectMetric={selectMetric}
                  deselectMetric={deselectMetric}
                />
              ))}
            </div>
          ))}
        </div>
        <div>
          {secondMetricsHalf.map((metric) => (
            <div key={metric.id}>
              <MetricSelection
                isSelected={selectedMetricsSet.has(metric.id)}
                metric={metric}
                selectMetric={selectMetric}
                deselectMetric={deselectMetric}
              />
              {metric.children?.map((childMetric) => (
                <MetricSelection
                  key={childMetric.id}
                  className="ml-6"
                  isChild
                  isSelected={selectedMetricsSet.has(childMetric.id)}
                  metric={childMetric}
                  selectMetric={selectMetric}
                  deselectMetric={deselectMetric}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

// TODO: Add tests
const divideIntoTwoColumns = (
  arr: SalesPageMetricWithChildren[],
): [SalesPageMetricWithChildren[], SalesPageMetricWithChildren[]] => {
  if (arr.length === 1) {
    return [arr, []];
  }

  const totalCount = arr.reduce(
    (count, obj) => count + 1 + (obj.children ? obj.children.length : 0),
    0,
  );
  const middleIndex = Math.ceil(totalCount / 2);

  let currentCount = 0;
  const firstHalf: SalesPageMetricWithChildren[] = [];
  const secondHalf: SalesPageMetricWithChildren[] = [];

  for (const obj of arr) {
    const objSize = 1 + (obj.children ? obj.children.length : 0);
    if (currentCount + objSize <= middleIndex) {
      firstHalf.push(obj);
      currentCount += objSize;
    } else {
      secondHalf.push(obj);
    }
  }

  return [firstHalf, secondHalf];
};
